import { uxrr } from '@zyno-io/uxrr-client';
import { ref } from 'vue';

import { GlobalState } from '@/global';

// The support site embedded as an iframe in the help drawer — the whole help
// experience (search, docs, browse, and Contact). We drill the context we capture
// for support (session id, current page, signed-in user) into the URL as query
// params; the support site stashes them and feeds them into the Freshdesk form
// whenever the user hits Contact. See zyno-support (captureEmbeddedContext).
// Override VITE_APP_SUPPORT_URL (e.g. http://localhost:5174) for local testing.
const SUPPORT_URL = (import.meta.env.VITE_APP_SUPPORT_URL || 'https://support.zyno.app').replace(/\/+$/, '');

/**
 * Absolute URL to a page on the support site. For links that open support content in a new tab
 * rather than the embedded help drawer, which always opens at the site root.
 */
export function supportUrl(path: string): string {
    return `${SUPPORT_URL}${path}`;
}

/** Whether the help drawer is currently visible. */
export const helpOpen = ref(false);
/** True once opened at least once — gates the kept-alive iframe so it loads lazily. */
export const helpEverOpened = ref(false);
/** The iframe src, built once on first open so reopening never reloads/loses the form. */
export const helpSrc = ref('');

// Drill the context we already capture for support (session id, current page, and
// the signed-in user) into the embed URL. The support site embeds it in Freshdesk.
function buildSupportUrl(): string {
    const params = new URLSearchParams();
    if (uxrr.sessionId) params.set('sid', uxrr.sessionId);
    params.set('ctx', location.href);

    const user = GlobalState.user.value;
    if (user) {
        const name = `${user.firstName} ${user.lastName}`.trim();
        if (name) params.set('name', name);
        if (user.email) params.set('email', user.email);
    }

    // Cache-buster so the drawer always loads the freshly-deployed support site.
    params.set('t', Date.now().toString());

    return `${SUPPORT_URL}/?${params.toString()}`;
}

export function openHelp(): void {
    // Build the URL once and keep the iframe mounted for the session, so switching
    // away and back never reloads the form or loses what the user has typed.
    if (!helpEverOpened.value) {
        helpSrc.value = buildSupportUrl();
        helpEverOpened.value = true;
    }
    helpOpen.value = true;
}

export function closeHelp(): void {
    helpOpen.value = false;
}
